import { utils } from 'cesg-shared';
import { computed, reactive, watch } from 'vue';
import type {
  ProjectOrganisationExternalActionEvent,
  UseProjectOrganisationExternalLogicProps,
} from '../types';
import { createProjectOrganisationExternalStore } from './createProjectOrganisationExternalStore';

export type { UseProjectOrganisationExternalLogicProps };

export const useProjectOrganisationExternalLogic = (
  props: UseProjectOrganisationExternalLogicProps,
  emit: (event: ProjectOrganisationExternalActionEvent) => void,
) => {
  const store = createProjectOrganisationExternalStore({
    name: props.name,
    stakeholders: props.stakeholders,
    initialMode: props.isReadOnly ? 'read-only' : 'normal',
  });

  const permissions = computed(() => {
    const isEditing = store.mode.value === 'edit';
    const isReadOnly = store.mode.value === 'read-only';
    return {
      editToggle: isReadOnly ? ('hidden' as const) : ('visible' as const),
      cancelButton: isEditing ? ('visible' as const) : ('hidden' as const),
      downloadButton: isEditing ? ('hidden' as const) : ('visible' as const),
      stakeholderInputs: isEditing ? ('edit' as const) : ('read-only' as const),
      moveButtons: isEditing ? ('visible' as const) : ('hidden' as const),
      deleteButton: isEditing ? ('visible' as const) : ('hidden' as const),
      addRow: isEditing ? ('visible' as const) : ('hidden' as const),
    };
  });

  watch(
    () => ({ name: props.name, stakeholders: props.stakeholders }),
    (newData) => {
      if (store.mode.value !== 'edit' && store.mode.value !== 'saving') {
        store.dispatch({
          type: 'set-organisation',
          name: newData.name,
          stakeholders: newData.stakeholders,
        });
      }
    },
    { deep: true },
  );

  watch(
    () => props.isSaving,
    (saving) => {
      if (!saving && store.mode.value === 'saving') {
        store.dispatch({ type: 'set-mode', mode: 'normal' });
      }
    },
  );

  watch(
    () => props.isReadOnly,
    (isReadOnly) => {
      if (isReadOnly) {
        store.dispatch({ type: 'set-mode', mode: 'read-only' });
      } else if (store.mode.value === 'read-only') {
        store.dispatch({ type: 'set-mode', mode: 'normal' });
      }
    },
  );

  const state = reactive({
    organisation: store.organisation,
    mode: computed(() => store.mode.value),
    newEntry: store.newEntry,
    hasChanges: computed(
      () =>
        store.snapshot !== null &&
        JSON.stringify(store.organisation.stakeholders) !==
          JSON.stringify(store.snapshot),
    ),
    permissions: computed(() => permissions.value),
  });

  const action = {
    toggleEdit: () => {
      if (store.mode.value === 'normal') {
        store.dispatch({ type: 'take-snapshot' });
        store.dispatch({ type: 'set-mode', mode: 'edit' });
      } else if (store.mode.value === 'edit') {
        emit({
          type: 'save',
          data: {
            name: store.organisation.name,
            stakeholders: store.organisation.stakeholders.map(
              ({ isNew, ...s }, i) => ({
                ...s,
                id: isNew ? undefined : s.id,
                order: i,
              }),
            ),
          },
        });
        store.dispatch({ type: 'set-mode', mode: 'saving' });
      }
    },

    cancel: () => {
      store.dispatch({ type: 'restore-snapshot' });
      store.dispatch({ type: 'set-mode', mode: 'normal' });
    },

    submitNew: () => {
      store.dispatch({ type: 'submit-new' });
    },

    delete: (id: string) => {
      store.dispatch({ type: 'delete-stakeholder', id });
    },

    moveUp: (id: string) => {
      store.dispatch({ type: 'move-up', id });
    },

    moveDown: (id: string) => {
      store.dispatch({ type: 'move-down', id });
    },

    update: (id: string, field: string, value: string) => {
      store.dispatch({ type: 'update-stakeholder', id, field, value });
    },

    downloadCsv: () => {
      const csv = utils.buildCsv(
        ['role', 'name', 'email', 'phone'],
        store.organisation.stakeholders.map((s) => ({
          role: s.role,
          name: s.name,
          email: s.email,
          phone: s.phone,
        })),
      );
      const safeFilename = store.organisation.name.replace(/[/\\:*?"<>|]/g, '_');
      utils.downloadFile(new Blob([csv], { type: 'text/csv' }), `${safeFilename}.csv`);
    },
  };

  return { state, action };
};
